// TranslationAnalysisModal.js

import React, { useState, useEffect, useCallback } from 'react';
import styles from './TranslationAnalysisModal.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';

const TranslationAnalysisModal = ({ isOpen, onClose, onConfirm, translationData, isLoading }) => {
    const [translatedSubject, setTranslatedSubject] = useState('');
    const [translatedBody, setTranslatedBody] = useState('');

    // Escape key handler
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Escape') onClose();
    }, [onClose]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            if (translationData) {
                setTranslatedSubject(translationData.translated_subject || '');
                setTranslatedBody(translationData.translated_body || '');
            }
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, handleKeyDown, translationData]);

    // Animation state for smooth entry/exit
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => setIsMounted(true), 10);
            return () => clearTimeout(timer);
        } else {
            setIsMounted(false);
        }
    }, [isOpen]);

    // Focus management on open
    useEffect(() => {
        if (isOpen && isMounted) {
            const firstInput = document.querySelector(`.${styles.modalContent} input`);
            if (firstInput) firstInput.focus();
        }
    }, [isOpen, isMounted]);

    if (!isOpen) return null;

    const handleConfirm = () => {
        onConfirm({ subject: translatedSubject, body: translatedBody });
    };

    return (
        <div
            className={`${styles.modalOverlay} ${isMounted ? styles.show : ''}`}
            onClick={onClose}
            aria-hidden={!isOpen}
        >
            <div
                className={`${styles.modalContent} ${isMounted ? styles.show : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                    <h3>Validate AI-Generated Translation</h3>
                    <button
                        onClick={onClose}
                        className={styles.closeButton}
                        aria-label="Close"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {isLoading ? (
                    <div className={styles.loadingContainer} aria-live="polite">
                        <div className={styles.spinner}></div>
                        <p>Translating...</p>
                    </div>
                ) : !translationData ? (
                    <div className={styles.errorContainer} aria-live="assertive">
                        <h4>Translation Not Available</h4>
                        <p>Could not load the translation. Please try again.</p>
                    </div>
                ) : (
                    <>
                        {/* --- MAIN CONTENT: SIDE-BY-SIDE COMPARISON --- */}
                        <div className={styles.comparisonGrid}>
                            {/* Column 1: Translated Draft (Editable) */}
                            <div className={styles.draftColumn}>
                                <h4>Translated Draft (Editable)</h4>
                                <div className={styles.draftEditor}>
                                    <textarea
                                        id="translatedSubject"
                                        row="1"
                                        value={translatedSubject}
                                        onChange={(e) => setTranslatedSubject(e.target.value)}
                                        className={styles.subjectInput}
                                    />
                                    <textarea
                                        id="translatedBody"
                                        value={translatedBody}
                                        onChange={(e) => setTranslatedBody(e.target.value)}
                                        className={styles.bodyTextarea}
                                    />
                                </div>
                            </div>

                            {/* Column 2: Rationale (Read-Only) */}
                            <div className={styles.draftColumn}>
                                <h4>Translation Rationale</h4>
                                <div className={styles.readOnlyContent}>
                                    <ReactMarkdown>{translationData.translation_rationale || "No rationale provided."}</ReactMarkdown>
                                </div>
                            </div>
                        </div>

                        {/* --- FOOTER: ACTION BUTTONS --- */}
                        <div className={styles.modalFooter}>
                            <button onClick={onClose} className={styles.footerButton}>
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirm}
                                className={`${styles.footerButton} ${styles.confirmButton}`}
                            >
                                <FontAwesomeIcon icon={faCheck} style={{ marginRight: '0.5rem' }} />
                                Confirm & Use Translation
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default TranslationAnalysisModal;